import { useEffect, useState, useRef } from "react";

import { tracking } from "@iwoca/frontend-tracking-library";
import { AlertBox } from "@iwoca/orion";
import { Trans } from "@lingui/react/macro";
import { useSearchParams, useNavigate } from "react-router";

import logger from "../../utils/logger";
import { redirectUser } from "../authentication";
import { verifyMagicLink } from "../authentication-cognito-magic-link";
import { getRedirectToLink } from "./getRedirectLink";
import styles from "../CommonLogin.module.css";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import LoginContainer from "../LoginContainer";

type VerificationStatus =
  | "verifying"
  | "success"
  | "expired"
  | "invalid"
  | "used"
  | "error";

const MagicLinkCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const verifyLink = async () => {
      // Extract parameters from URL
      const username = searchParams.get("username");
      const signature = searchParams.get("signature");
      const issuedAt = searchParams.get("issuedAt");
      const expiresAt = searchParams.get("expiresAt");

      // Generate unique key for this verification attempt
      const verificationKey = `magiclink_verifying_${signature}`;

      // Prevent double-execution in React StrictMode (development)
      // Use sessionStorage as it persists across component re-mounts
      if (sessionStorage.getItem(verificationKey)) {
        console.log(
          "Verification already in progress, skipping duplicate call",
        );
        return;
      }
      sessionStorage.setItem(verificationKey, "true");

      // Validate required parameters
      if (!username || !signature || !issuedAt || !expiresAt) {
        logger.error("Missing required magic link parameters");
        setStatus("invalid");
        setErrorMessage("Invalid magic link. Please request a new one.");
        tracking.ampli.loginFailed();
        return;
      }

      // Parse timestamps
      const issuedAtNum = parseInt(issuedAt, 10);
      const expiresAtNum = parseInt(expiresAt, 10);

      if (isNaN(issuedAtNum) || isNaN(expiresAtNum)) {
        logger.error("Invalid timestamp in magic link");
        setStatus("invalid");
        setErrorMessage("Invalid magic link. Please request a new one.");
        tracking.ampli.loginFailed();
        return;
      }

      // Client-side expiry check
      const now = Date.now();
      if (now > expiresAtNum) {
        console.log("Magic link has expired");
        setStatus("expired");
        tracking.ampli.loginFailed();
        return;
      }

      // Try to retrieve stored session (same browser)
      let session: string | undefined;
      try {
        const storedData = sessionStorage.getItem("cognitoMagicLinkSession");

        if (storedData) {
          const parsed = JSON.parse(storedData);

          // Verify it's for the same email and not too old (max 1 hour)
          if (parsed.email === username && now - parsed.timestamp < 3600000) {
            session = parsed.session;
            console.log("Using stored session for same-browser verification");
          } else {
            console.log("Stored session expired or for different email");
            sessionStorage.removeItem("cognitoMagicLinkSession");
          }
        }
      } catch (error: any) {
        logger.error("Error retrieving stored session", {
          error: error?.message || "Unknown error",
        });
        // Continue without session - will work cross-browser
      }

      // Verify magic link with Cognito
      try {
        const result = await verifyMagicLink(
          username,
          signature,
          issuedAtNum,
          expiresAtNum,
          session,
        );

        if (result.success && result.tokens) {
          console.log("Magic link verification successful");
          setStatus("success");
          tracking.ampli.loginSucceeded();

          // Clear stored session and verification flag
          sessionStorage.removeItem("cognitoMagicLinkSession");
          sessionStorage.removeItem(verificationKey);

          // Redirect with tokens
          redirectUser({
            redirectUrl: getRedirectToLink(),
            tokens: result.tokens,
          });
        } else if (result.error) {
          logger.error("Magic link verification failed", {
            error: result.error.message,
            code: result.error.code,
          });

          // Clear verification flag on error
          sessionStorage.removeItem(verificationKey);

          // Map error codes to statuses
          if (result.error.code === "LinkExpired") {
            setStatus("expired");
          } else if (result.error.code === "NotAuthorizedException") {
            setStatus("used");
          } else {
            setStatus("error");
            setErrorMessage(result.error.message || "Verification failed");
          }

          tracking.ampli.loginFailed();
        }
      } catch (error: any) {
        logger.error("Magic link verification error", {
          error: error?.message || "Unknown error",
        });

        // Clear verification flag on error
        sessionStorage.removeItem(verificationKey);

        setStatus("error");
        setErrorMessage(error.message || "An unexpected error occurred");
        tracking.ampli.loginError();
      }
    };

    verifyLink();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    navigate("/login/link");
  };

  if (status === "verifying") {
    return (
      <LoginContainer>
        <div
          className={styles.loginWithLinkForm}
          data-testid="magic-link-verifying"
        >
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <LoadingSpinner />
            <h2 style={{ marginTop: "20px" }}>
              <Trans>Verifying your magic link...</Trans>
            </h2>
            <p style={{ color: "#666", marginTop: "10px" }}>
              <Trans>Please wait while we sign you in</Trans>
            </p>
          </div>
        </div>
      </LoginContainer>
    );
  }

  if (status === "success") {
    return (
      <LoginContainer>
        <div
          className={styles.loginWithLinkForm}
          data-testid="magic-link-success"
        >
          <AlertBox variant="success" className={styles.goodBox}>
            <h2>
              <Trans>Success!</Trans>
            </h2>
            <p>
              <Trans>Loading...</Trans>
            </p>
          </AlertBox>
        </div>
      </LoginContainer>
    );
  }

  if (status === "expired") {
    return (
      <LoginContainer>
        <div
          className={styles.loginWithLinkForm}
          data-testid="magic-link-expired"
        >
          <AlertBox variant="failure" className={styles.errorBox}>
            <h2>
              <Trans>Link Expired</Trans>
            </h2>
            <p>
              <Trans>This link has expired. Please request a new one.</Trans>
            </p>
          </AlertBox>
          <button
            onClick={handleRetry}
            className={styles.submitButton}
            style={{ marginTop: "20px" }}
          >
            <Trans>Start again with a new link</Trans>
          </button>
        </div>
      </LoginContainer>
    );
  }

  if (status === "used") {
    return (
      <LoginContainer>
        <div className={styles.loginWithLinkForm} data-testid="magic-link-used">
          <AlertBox variant="failure" className={styles.errorBox}>
            <h2>
              <Trans>Looks like you've used that link before</Trans>
            </h2>
            <p>
              <Trans>You can only use each link once, unfortunately.</Trans>
            </p>
          </AlertBox>
          <button
            onClick={handleRetry}
            className={styles.submitButton}
            style={{ marginTop: "20px" }}
          >
            <Trans>Start again with a new link</Trans>
          </button>
        </div>
      </LoginContainer>
    );
  }

  if (status === "invalid") {
    return (
      <LoginContainer>
        <div
          className={styles.loginWithLinkForm}
          data-testid="magic-link-invalid"
        >
          <AlertBox variant="failure" className={styles.errorBox}>
            <h2>
              <Trans>Hmm, something's gone wrong</Trans>
            </h2>
            <p>
              {errorMessage || (
                <Trans>
                  Sorry about that; we're looking into it. Try again later?
                </Trans>
              )}
            </p>
          </AlertBox>
          <button
            onClick={handleRetry}
            className={styles.submitButton}
            style={{ marginTop: "20px" }}
          >
            <Trans>Start again with a new link</Trans>
          </button>
        </div>
      </LoginContainer>
    );
  }

  // Generic error
  return (
    <LoginContainer>
      <div className={styles.loginWithLinkForm} data-testid="magic-link-error">
        <AlertBox variant="failure" className={styles.errorBox}>
          <h2>
            <Trans>Verification Failed</Trans>
          </h2>
          <p>
            {errorMessage || (
              <Trans>Something went wrong. Please try again.</Trans>
            )}
          </p>
        </AlertBox>
        <button
          onClick={handleRetry}
          className={styles.submitButton}
          style={{ marginTop: "20px" }}
        >
          <Trans>Retry</Trans>
        </button>
      </div>
    </LoginContainer>
  );
};

export default MagicLinkCallback;
